import { LuSend } from "react-icons/lu";
import { Check } from "@mui/icons-material";
import { FaRegClock } from "react-icons/fa6";
import { useTranslation } from "react-i18next";
import { Stack, Typography } from "@mui/material";

import {
  DeliveryOption,
  useDeliveryMethodSelectorState,
} from "./DeliveryMethodSelector/DeliveryMethodSelector";
import {
  calcPickerValue,
  useDeliveryMethodDatePicker,
} from "./DeliveryMethodSelector/DeliveryMethodDatePicker";

import type { RecipientDetailsType } from "./usePurchaseGiftCard";

interface ConfirmGiftCardContentProps extends RecipientDetailsType {}

export function ConfirmGiftCardContent(props: ConfirmGiftCardContentProps) {
  const { email, message } = props;

  const { t, i18n } = useTranslation();
  const { deliveryDate } = useDeliveryMethodDatePicker();
  const { selectedDeliveryMethod } = useDeliveryMethodSelectorState();

  const pickerValueFormat =
    i18n.language === "fr" ? "DD MMMM YYYY" : "dddd, MMM DD, YYYY";

  return (
    <Stack direction="column" spacing={2}>
      <Typography variant="body1">
        {t("giftCard.checkout.confirmationModal.description") ||
          "Please review the details of your gift card purchase before confirming."}
      </Typography>

      <Stack direction="row" spacing={0.5} alignItems={"center"}>
        <Typography variant="subtitle2">
          {t("giftCard.checkout.confirmationModal.recipientEmailLabel") ||
            "Recipient's Email:"}
        </Typography>
        <Typography variant="body1" sx={{ textDecoration: "underline" }}>
          {email}
        </Typography>
      </Stack>

      {message ? (
        <Stack direction="row" spacing={0.5}>
          <Typography variant="subtitle2">
            {t("giftCard.checkout.confirmationModal.messageLabel") ||
              "Message:"}
          </Typography>
          <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
            {message}
          </Typography>
        </Stack>
      ) : null}

      {selectedDeliveryMethod === "sendNow" ? (
        <DeliveryOption
          icon={<LuSend />}
          title={
            t("giftCard.checkout.deliveryMethodSelector.sendNowTitle") ||
            "Send Now"
          }
          description={
            t("giftCard.checkout.deliveryMethodSelector.sendNowDescription") ||
            "Email is sent immediately"
          }
          isSelected={true}
          slotSx={{
            button: {
              cursor: "default !important",
            },
          }}
          onClick={() => {}}
        />
      ) : null}

      {selectedDeliveryMethod === "sendLater" && deliveryDate ? (
        <Stack spacing={2} textAlign={"left"} direction={"column"}>
          <DeliveryOption
            icon={<FaRegClock />}
            title={
              t("giftCard.checkout.deliveryMethodSelector.sendLaterTitle") ||
              "Send Later"
            }
            description={
              t(
                "giftCard.checkout.deliveryMethodSelector.sendLaterDescription"
              ) || "Schedule for a specific date"
            }
            isSelected={true}
            slotSx={{
              button: {
                cursor: "default !important",
              },
              root: {
                width: "100%",
              },
            }}
            onClick={() => {}}
          />

          <Stack spacing={0.5} direction="row" alignItems={"center"}>
            <Typography variant="subtitle2">
              {t("giftCard.checkout.confirmationModal.deliveryDateLabel") ||
                "Delivery Date:"}
            </Typography>
            <Typography variant="body1" sx={{ textDecoration: "underline" }}>
              {calcPickerValue(deliveryDate || "", "MM-DD-YYYY")
                ?.locale(i18n.language)
                .format(pickerValueFormat) || ""}
            </Typography>
          </Stack>
        </Stack>
      ) : null}
    </Stack>
  );
}
